{
  "$schema": "http://json-schema.org/draft-07/schema#",
  "$id": "qualification",
  "x-cli-editable": true,
  "x-feature": true,
  "x-tier": "advanced",
  "title": "Qualification",
  "description": "Full visitor qualification funnel: enrichment, interest signals, and visitor profiling.",
  "type": "object",
  "properties": {
    "enrich_all_visitors": {
      "ui:sectionTitle": "Enrichment",
      "ui:sectionDescription": "How visitors are enriched and profiled.",
      "ui:sectionIcon": "Users",
      "type": "boolean",
      "ui:description": "Identify every visitor's company as soon as they load the page, using all enrichment sources (IP lookup and third-party de-anonymization). When off, visitors are identified only once they engage with the widget (send a message).",
      "x-access": "staff"
    },
    "identification_requires_consent": {
      "type": "boolean",
      "default": false,
      "ui:title": "Identify only after cookie consent",
      "ui:description": "Rose stays visible to every visitor, but identifies nobody (company or person) until your cookie banner reports consent through window.InboundXWidget.setIdentificationConsent(true). Turn on when your banner must gate visitor identification. Consent comes from the domain's cookie banner, so set this for the whole domain, not for a path workspace.",
      "x-access": "staff"
    },
    "person_identity": {
      "type": "object",
      "x-access": "staff",
      "ui:title": "Identify Visitors",
      "ui:description": "Identify the individual person behind a visit — name, work email, role — on top of company enrichment. Scope follows Enrich All Visitors: on means every page load, off means only after the visitor engages with the widget.",
      "properties": {
        "enabled": {
          "type": "boolean",
          "default": false,
          "ui:title": "Enable person-level identification",
          "ui:description": "Off by default. When on, eligible visitors are matched to a person through the contact-level providers below."
        },
        "mode": {
          "type": "string",
          "enum": [
            "waterfall",
            "shadow_all"
          ],
          "default": "shadow_all",
          "ui:title": "Execution mode",
          "ui:description": "waterfall stops after the first matched provider wins. shadow_all runs all enabled providers and records each one's evidence for comparison."
        },
        "providers": {
          "type": "object",
          "ui:title": "Providers",
          "ui:description": "Turn each identification vendor on or off.",
          "properties": {
            "rb2b": {
              "type": "boolean",
              "default": true,
              "ui:title": "RB2B",
              "ui:description": "Identify the person via RB2B (IP → hashed email → business profile). US traffic."
            },
            "vector": {
              "type": "boolean",
              "default": true,
              "ui:title": "Vector",
              "ui:description": "Identify the person via the Vector.co contact pixel. US traffic."
            }
          }
        }
      }
    },
    "interest_signals": {
      "type": "object",
      "ui:description": "Interest signal detection configuration",
      "x-access": "staff",
      "properties": {
        "signals": {
          "type": "array",
          "ui:description": "Interest signals detected by the LLM during conversation",
          "maxItems": 50,
          "items": {
            "type": "object",
            "properties": {
              "description": {
                "type": "string",
                "ui:widget": "textarea",
                "ui:description": "Detection criteria for the LLM",
                "maxLength": 1000,
                "minLength": 1
              },
              "name": {
                "type": "string",
                "ui:description": "Signal identifier (snake_case)",
                "maxLength": 100,
                "pattern": "^[a-z0-9_]+$"
              },
              "points": {
                "type": "integer",
                "ui:description": "Score weight (0-100)",
                "minimum": 0,
                "maximum": 100
              }
            }
          }
        },
        "threshold": {
          "type": [
            "integer",
            "null"
          ],
          "ui:description": "Total points threshold to trigger demo proposal",
          "minimum": 1,
          "maximum": 1000
        }
      }
    },
    "profiling": {
      "type": "object",
      "ui:title": "Visitor Qualification",
      "ui:description": "Collect information from visitors through forms. Define reusable fields in the Field Library, then compose forms that reference those fields.",
      "properties": {
        "enabled": {
          "type": "boolean",
          "ui:description": "When enabled, qualification forms are active."
        },
        "field_definitions": {
          "type": "object",
          "ui:title": "Field Library",
          "ui:description": "Reusable data fields shared across all forms. Define what data you want to collect — the AI extracts it from the visitor's answers.",
          "ui:addLabel": "Add field",
          "additionalProperties": {
            "type": "object",
            "properties": {
              "label": {
                "type": "string",
                "ui:title": "Display Label",
                "ui:description": "Short, readable name for this field shown in the visitor profile (e.g. 'Monthly traffic'). Leave empty to use the field's id.",
                "maxLength": 200
              },
              "description": {
                "type": "string",
                "ui:widget": "textarea",
                "ui:title": "Description",
                "ui:description": "What this field captures (e.g. 'Company sector, revenue, structure'). Used by the AI to extract the right information from the visitor's answers.",
                "maxLength": 2000,
                "minLength": 1
              },
              "type": {
                "type": "string",
                "enum": [
                  "freeform",
                  "predefined"
                ],
                "ui:title": "Answer Type",
                "ui:description": "Freeform: visitor answers in their own words. Predefined: visitor picks from a list of options.",
                "default": "freeform"
              },
              "extraction_prompt": {
                "type": "string",
                "ui:widget": "textarea",
                "ui:title": "Extraction Instructions",
                "ui:description": "Custom instructions for the AI to extract the answer from conversation. Leave empty to auto-generate from the question and options.",
                "x-access": "staff",
                "maxLength": 2000
              },
              "importance": {
                "type": "string",
                "enum": [
                  "low",
                  "normal",
                  "high",
                  "critical"
                ],
                "default": "normal",
                "ui:title": "Importance",
                "ui:description": "How strongly the agent should push to collect this field. 'normal' (default) = elicit on natural openings. 'high' = prioritize over other pending fields when picking the one question to weave per turn. 'critical' = the answer surface meaningfully depends on this field (e.g. jurisdiction, country, segment) — the agent weaves the question even on weaker openings during substantive product turns, and asks it first in the CTA gate. 'low' = only ask when there is an obvious opening, deprioritized against the others.",
                "x-access": "staff"
              },
              "options": {
                "type": "array",
                "ui:title": "Answer Options",
                "ui:description": "Available choices for predefined fields. Add the text visitors see in every language configured under Brand & tone. Each translated label maps to the same stable CRM value.",
                "ui:show-when": { "type": "predefined" },
                "ui:widget": "localized-qualification-options",
                "items": {
                  "type": "object",
                  "properties": {
                    "label": {
                      "type": "string",
                      "ui:title": "Legacy Option Label",
                      "ui:description": "Fallback label kept for existing configurations.",
                      "ui:hidden": true,
                      "maxLength": 200
                    },
                    "labels": {
                      "type": "object",
                      "ui:title": "Visitor Labels",
                      "ui:description": "The option text shown to visitors in each configured language.",
                      "additionalProperties": {
                        "type": "string",
                        "minLength": 1,
                        "maxLength": 200
                      }
                    },
                    "value": {
                      "type": "string",
                      "ui:title": "CRM Value",
                      "ui:description": "Stable value sent to your CRM and used in personalized URLs. It stays the same in every language. Use a short URL-safe slug with letters, digits, _ . - or ~.",
                      "pattern": "^[A-Za-z0-9_.~-]*$"
                    }
                  },
                  "required": [],
                  "allOf": [
                    {
                      "$comment": "Backward compatibility: legacy options may keep label, while localized options provide at least one locale-keyed label.",
                      "anyOf": [
                        {
                          "required": ["label"],
                          "properties": {
                            "label": { "minLength": 1 }
                          }
                        },
                        {
                          "required": ["labels"],
                          "properties": {
                            "labels": { "minProperties": 1 }
                          }
                        }
                      ]
                    },
                    {
                      "$comment": "Localized options must keep extraction and CRM submission locale-independent by providing a stable value.",
                      "if": { "required": ["labels"] },
                      "then": {
                        "required": ["value"],
                        "properties": {
                          "value": { "minLength": 1 }
                        }
                      }
                    }
                  ]
                }
              },
              "enrichment_field": {
                "type": "string",
                "enum": [
                  "company_name",
                  "sector",
                  "sub_sector",
                  "domain",
                  "email",
                  "company_linkedin_url",
                  "location",
                  "employee_count",
                  "country",
                  "job_title",
                  "linkedin_url"
                ],
                "ui:title": "Enrichment Field",
                "ui:description": "Optional. If set, this field is auto-satisfied when IP enrichment already knows this visitor attribute, so the agent won't re-ask it (and CTA URLs fill from the enriched value). An explicit answer from the visitor always overrides the enriched value.",
                "x-access": "staff"
              }
            },
            "required": [],
            "allOf": [
              {
                "$comment": "Saved field definitions must have description and type. Enforced by AJV (backoffice) and jsonschema Draft7 (rose-config CLI). Kept under allOf so the codegen leaves the Pydantic model permissive — existing client overrides predating this rule still validate at resolver load time and only fail when the user re-saves through the backoffice or CLI.",
                "required": [
                  "description",
                  "type"
                ]
              },
              {
                "$comment": "Predefined fields must carry at least one option. Without options the AI extractor has no enum to map answers onto and silently fails to bind the visitor's answer to the field, which causes the agent to ask the same question again on the next turn.",
                "if": {
                  "properties": { "type": { "const": "predefined" } },
                  "required": ["type"]
                },
                "then": {
                  "required": ["options"],
                  "properties": {
                    "options": { "minItems": 1 }
                  }
                }
              }
            ]
          }
        },
        "forms": {
          "type": "object",
          "ui:title": "Forms",
          "ui:description": "Qualification forms that collect fields from visitors. Each form references fields from the Field Library above.",
          "ui:addLabel": "Add form",
          "additionalProperties": {
            "type": "object",
            "properties": {
              "form_type": {
                "type": "string",
                "enum": [
                  "qualification",
                  "in_chat_booking",
                  "post_conversion"
                ],
                "ui:title": "Form Type",
                "ui:description": "When this form collects data from visitors. Qualification: fields are collected during the conversation before the visitor clicks a CTA. Optionally gate the CTA until fields are collected using Blocking Fields. In-Chat Booking: fields are collected by the booking assistant after the visitor clicks a CTA, alongside their email. Post-Conversion: fields are collected on a thank-you page after the visitor has converted.",
                "default": "qualification"
              },
              "id": {
                "type": "string",
                "ui:description": "Unique form identifier.",
                "ui:hidden": true,
                "maxLength": 50
              },
              "preview": {
                "type": "boolean",
                "ui:title": "Preview Mode",
                "ui:description": "Only applies to post_conversion forms. When true, the form is hidden from visitors and only activates with ?rose_demo=1 in the URL. Ignored by the runtime on qualification and in_chat_booking forms.",
                "ui:show-when": { "form_type": "post_conversion" }
              },
              "field_groups": {
                "type": "array",
                "ui:title": "Question Groups",
                "ui:description": "Organize your questions into logical groups. The AI walks through them in order, transitioning naturally between topics. Keep it short — 3 to 5 questions total works best. Each answer helps your team prepare a more relevant demo or follow-up. For in-chat booking forms, only fields referenced as {{placeholders}} in the Personalized CTA URL are actually asked — list them here so the AI knows how to capture each one (description, options). Email is collected automatically.",
                "maxItems": 10,
                "items": {
                  "type": "object",
                  "properties": {
                    "label": {
                      "type": "string",
                      "ui:title": "Group Label",
                      "ui:description": "A short label so the AI transitions naturally between topics (e.g. 'About your company', 'Your project')."
                    },
                    "questions": {
                      "type": "array",
                      "ui:title": "Questions",
                      "ui:description": "The questions the AI asks, in order. Frame each question around the visitor's benefit — they're sharing context so the team can help them faster. Attach fields to capture structured data for your CRM.",
                      "maxItems": 20,
                      "items": {
                        "type": "object",
                        "properties": {
                          "text": {
                            "type": "string",
                            "ui:widget": "textarea",
                            "ui:title": "Question Text",
                            "ui:description": "The question as the visitor will see it. Frame it so the visitor understands why answering helps them — e.g. 'What's your main use case? This helps us tailor the demo to what matters most to you.' rather than just 'What's your use case?'",
                            "maxLength": 2000,
                            "minLength": 1
                          },
                          "fields": {
                            "type": "array",
                            "ui:widget": "field-definition-keys",
                            "ui:title": "Attached Fields",
                            "ui:description": "Fields to extract from the answer. Leave empty for a purely conversational question.",
                            "items": {
                              "type": "string"
                            }
                          }
                        },
                        "required": [
                          "text"
                        ]
                      }
                    }
                  },
                  "required": [
                    "label"
                  ]
                }
              },
              "cta_id": {
                "type": "string",
                "ui:widget": "cta-select",
                "ui:title": "Linked CTA",
                "ui:description": "The CTA this form is linked to. Must match a CTA ID from the CTAs section. Used by qualification forms (to gate the CTA) and in-chat booking forms (to collect fields after the CTA is clicked).",
                "ui:show-when": { "form_type": ["qualification", "in_chat_booking"] },
                "maxLength": 50
              },
              "cta_blocking_fields": {
                "type": "array",
                "ui:widget": "field-definition-keys",
                "ui:title": "CTA-Blocking Fields",
                "ui:description": "Field names that must be collected before the CTA is shown. The agent asks these questions before showing the call-to-action.",
                "ui:show-when": { "form_type": "qualification" },
                "items": {
                  "type": "string"
                }
              },
              "cta_url_template": {
                "type": "string",
                "ui:title": "Personalized CTA URL",
                "ui:description": "URL with {{field_name}} placeholders. Filled from collected fields to create a personalized CTA link. Example: https://example.com/book?size={{company_size}}&email={{email}}. For in-chat booking forms, the placeholders also drive what the booking assistant asks the visitor — alongside email, which is always collected.",
                "ui:show-when": { "form_type": ["qualification", "in_chat_booking"] },
                "pattern": "^https?://"
              },
              "fallback_url": {
                "type": "string",
                "format": "uri",
                "ui:title": "Fallback URL",
                "ui:description": "URL used when not all placeholders could be filled.",
                "ui:show-when": { "form_type": ["qualification", "in_chat_booking"] },
                "pattern": "\\.[a-zA-Z]{2,}"
              },
              "trigger": {
                "type": "object",
                "ui:title": "Activation Trigger",
                "ui:description": "When to auto-open this form. Either configure thank-you page URLs, or listen for a form submission on the current page (for inline-confirmation forms that don't redirect).",
                "ui:show-when": { "form_type": "post_conversion" },
                "properties": {
                  "type": {
                    "type": "string",
                    "enum": [
                      "post_conversion"
                    ],
                    "ui:hidden": true
                  },
                  "pages": {
                    "type": "array",
                    "ui:title": "Thank-You Page URLs",
                    "ui:description": "URL path patterns for your post-conversion pages. Use * as wildcard (e.g. /thank-you* matches /thank-you, /thank-you-demo, etc.). The form auto-opens on these pages right after the visitor converts.",
                    "items": {
                      "type": "string"
                    }
                  },
                  "on_form_submit": {
                    "type": "object",
                    "ui:title": "Open After Form Submit (same page)",
                    "ui:description": "Use this for landing pages where the form shows an inline confirmation instead of redirecting to a thank-you URL (e.g. Unbounce AJAX forms). When a form submit is detected and matches one of the configured form action patterns, the qualification form opens immediately on the same page.",
                    "properties": {
                      "enabled": {
                        "type": "boolean",
                        "ui:title": "Enable Form-Submit Activation",
                        "ui:description": "Turn on to activate the qualification form from a form-submit event instead of (or in addition to) a thank-you page URL.",
                        "default": false
                      },
                      "pages": {
                        "type": "array",
                        "ui:title": "Pages Where Form Lives",
                        "ui:description": "URL path patterns for the pages that contain the form we're listening to. Same syntax as Thank-You Page URLs. Only submits detected on these pages activate the qualification form.",
                        "ui:show-when": { "enabled": true },
                        "items": {
                          "type": "string"
                        }
                      },
                      "form_action_matches": {
                        "type": "array",
                        "ui:title": "Required Form Action URL Patterns",
                        "ui:description": "Safety gate — only activate when the submitted form's action URL matches one of these patterns (star is a wildcard). This prevents false positives from other forms on the same page (newsletter signups, search boxes, etc.). Inspect the form in DevTools to find the exact action URL. Example: https://get.example.com/fsg — matches regardless of query string.",
                        "ui:show-when": { "enabled": true },
                        "items": {
                          "type": "string"
                        }
                      }
                    },
                    "required": [
                      "enabled",
                      "pages",
                      "form_action_matches"
                    ]
                  }
                }
              },
              "welcome_title": {
                "type": "string",
                "default": "Help us prepare your follow-up",
                "ui:title": "Welcome Title",
                "ui:description": "Bold heading shown above the welcome message — auto-translated to the visitor's language. Keep it short and action-oriented. Example: 'Help us prepare your demo'.",
                "ui:show-when": { "form_type": "post_conversion" },
                "maxLength": 200
              },
              "welcome_message": {
                "type": "string",
                "ui:widget": "textarea",
                "ui:title": "Welcome Message",
                "ui:description": "The first message visitors see when the form opens — auto-translated to their language. Frame it around their benefit: a few quick questions so the team can prepare the most relevant demo or follow-up. Example: 'Thanks for booking! A few quick questions so we can tailor your demo to your exact needs.'",
                "ui:show-when": { "form_type": "post_conversion" },
                "maxLength": 2000
              },
              "estimated_duration": {
                "type": "string",
                "ui:title": "Estimated Duration",
                "ui:description": "Shown alongside the welcome message. Visitors are more likely to complete the form when they know it's quick. Example: '2 minutes', 'under 1 minute'.",
                "ui:show-when": { "form_type": "post_conversion" },
                "maxLength": 100
              },
              "completion_message": {
                "type": "string",
                "ui:widget": "textarea",
                "ui:title": "Completion Message",
                "ui:description": "Instructions for the AI when all questions are answered — it will generate a thank-you message in the visitor's language. Tell the AI what to say: who will follow up, when, and what the visitor can expect. Example: 'Thank the visitor and let them know a sales rep will reach out within 24 hours with a demo tailored to their answers.'",
                "ui:show-when": { "form_type": "post_conversion" },
                "maxLength": 2000
              },
              "on_complete": {
                "type": "object",
                "ui:title": "On Completion",
                "ui:description": "What happens when all questions are answered — send the collected data to your CRM or automation tool via webhook, and optionally let the AI score each lead automatically.",
                "ui:show-when": { "form_type": ["qualification", "post_conversion"] },
                "properties": {
                  "webhook_endpoint": {
                    "type": "string",
                    "ui:title": "Webhook URL",
                    "ui:description": "URL to receive the collected data when the form is completed. Leave empty until the CRM webhook is wired.",
                    "default": "",
                    "anyOf": [
                      { "const": "" },
                      { "format": "uri" }
                    ]
                  },
                  "webhook_secret": {
                    "type": "string",
                    "ui:title": "Webhook Secret",
                    "ui:description": "Shared secret for HMAC-SHA256 webhook signature verification. Sent as X-Webhook-Signature header. Copy this value into your endpoint to verify webhook authenticity.",
                    "readOnly": true,
                    "maxLength": 256,
                    "default": ""
                  },
                  "ai_scoring": {
                    "type": "object",
                    "x-tier": "advanced",
                    "ui:title": "AI Lead Scoring",
                    "ui:tooltip": "Automatically summarize and score each lead after the conversation.",
                    "ui:description": "When enabled, the AI analyzes every completed conversation and produces a summary and a score (hot, warm, cold, or redirect). Both are sent in the webhook payload so your CRM or automation tool can route leads without manual review.",
                    "properties": {
                      "enabled": {
                        "type": "boolean",
                        "ui:tooltip": "Enable AI lead scoring.",
                        "ui:description": "When turned on, every completed qualification form triggers an AI analysis before the webhook fires. The AI generates a conversation summary and a lead score that are included in the webhook data.",
                        "default": false
                      },
                      "summary_prompt": {
                        "type": "string",
                        "ui:widget": "textarea",
                        "ui:title": "Summary Instructions",
                        "ui:tooltip": "Tell the AI how to summarize each conversation.",
                        "ui:description": "The AI reads the full conversation and writes a summary following these instructions. The summary is sent in the webhook payload and also used as input for the scoring step. Customize to highlight what matters most to your sales team — e.g. budget, timeline, company size, or specific use case.",
                        "ui:show-when": { "enabled": true },
                        "maxLength": 5000,
                        "default": "Summarize this conversation in 2-3 sentences. Focus on: what the visitor is looking for, their company context, key requirements, and any urgency or timeline mentioned. Be factual and concise."
                      },
                      "scoring_prompt": {
                        "type": "string",
                        "ui:widget": "textarea",
                        "ui:title": "Scoring Instructions",
                        "ui:tooltip": "Define your criteria for each score level.",
                        "ui:description": "The AI reads the conversation summary and the collected form answers, then assigns one of four scores: hot, warm, cold, or redirect. Tailor the criteria to your sales process. Example: 'Score as hot if budget > 50k and timeline < 3 months. Score as redirect if the visitor is asking for customer support.'",
                        "ui:show-when": { "enabled": true },
                        "maxLength": 5000,
                        "default": "Score this lead based on buying intent and fit:\n- hot: Strong buying signals (budget confirmed, short timeline, explicit demo/trial request)\n- warm: Genuine interest with evaluation signals (comparing options, asking detailed questions, sharing business context)\n- cold: Early-stage research or low engagement (generic questions, minimal context shared)\n- redirect: Not a sales lead (support request, job inquiry, spam, off-topic)"
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  },
  "default": {
    "enrich_all_visitors": false,
    "identification_requires_consent": false,
    "person_identity": {
      "enabled": false,
      "mode": "shadow_all",
      "providers": {
        "rb2b": true,
        "vector": true
      }
    },
    "interest_signals": {
      "signals": [
        {
          "name": "explicit_demo_request",
          "points": 21,
          "description": "Visitor explicitly asks for a demo, to speak with sales, or to be contacted. Triggers: 'I want to talk to sales', 'Can I talk to sales?', 'I want a demo', 'Can I book a call?', 'Can I try?', 'I want to be contacted'. Strongest buying signal."
        },
        {
          "name": "pricing_request",
          "points": 6,
          "description": "Visitor asks about pricing or cost. Triggers: 'How much does it cost?', 'What is the price?', 'Price', 'I want a price range', 'Is it free?', 'What's your pricing', 'Do you have a price grid?', 'Can I see pricing?'. Visitor must explicitly ask about price/cost."
        },
        {
          "name": "visual_demo_needs",
          "points": 6,
          "description": "Visitor expresses need to see the product visually. Includes: requests to 'see', 'show me', 'examples', 'can you demonstrate', UI questions, questions about UI/dashboard/interface/reports. Visitor must explicitly ask to SEE or be SHOWN something."
        },
        {
          "name": "roi_business_value",
          "points": 4,
          "description": "Visitor asks about business outcomes, ROI, or implementation. Includes: revenue impact/ROI/business results questions, success metrics, implementation timeline, onboarding or training process questions. Must ask about business outcomes, not just features."
        },
        {
          "name": "competitor_comparison",
          "points": 4,
          "description": "Visitor explicitly compares the product with competitors or alternatives. Shows active evaluation phase. Visitor must name a specific competitor or alternative tool."
        },
        {
          "name": "case_study_request",
          "points": 4,
          "description": "Visitor explicitly asks for case studies, customer examples, success stories, or proof of results. Shows need for social proof and validation before purchase decision."
        },
        {
          "name": "technical_evaluation",
          "points": 4,
          "description": "Visitor asks technical questions showing deeper evaluation. Includes: technical questions with depth, increasingly specific/advanced questions, questions about specific integrations or compatibility, technical implementation details (APIs, SDKs, architecture)."
        },
        {
          "name": "use_case_sharing",
          "points": 4,
          "description": "Visitor shares specific business context, pain, or objectives. Includes: business context, pain/frustration, urgency/timeline, workflow description, industry mention, monthly traffic shared, clear objectives/KPIs. Visitor must SAY/SHARE the context explicitly."
        },
        {
          "name": "solution_complexity",
          "points": 4,
          "description": "Visitor discusses complex solution needs or sophisticated requirements. Includes: multiple complex requirements across platforms, multiple distinct use cases requiring custom architecture, complex multi-system integrations or technical constraints."
        },
        {
          "name": "generic_information",
          "points": 2,
          "description": "Visitor asks basic informational questions showing discovery/awareness phase (TOFU). Triggers: 'What is [product]?', 'What do you do?', 'Do you have [basic feature]?'. Must be genuine discovery questions."
        },
        {
          "name": "brief_answer",
          "points": 2,
          "description": "Visitor provides a very short or minimal response that answers a question but adds no meaningful context or buying signal. Includes one-word or low-information replies such as 'yes', 'no', 'ok', 'sure', email address, short confirmations."
        },
        {
          "name": "off_topic",
          "points": 0,
          "description": "Message is off-topic, spam, testing, customer support request, or completely unrelated to new product evaluation. Default for garbage messages and support requests."
        }
      ],
      "threshold": 21
    },
    "profiling": {
      "enabled": false,
      "field_definitions": {},
      "forms": {}
    }
  }
}
